import axios from "axios";
import {
  BASE_URL,
  UtcOffsetInSecond,
  AppVersion,
} from "../constants/userConstant";
import { getTokens } from "../utils/helper";
import { ERROR_MESSAGES } from "../constants/messages";
import { normalizeServiceError } from "../utils/serviceError";
import { clearAppStorage } from "../utils/authStorage";
import { toast } from "react-toastify";

const FORBIDDEN_REDIRECT_DELAY = 2000;

// Set while the post-login guest-cart sync is in flight (see authcontext.js syncGuestCart) -
// a 403 from that call shouldn't force-logout a user who just logged in successfully.
let forceLogoutSuppressed = false;

export const setForceLogoutSuppressed = (value) => {
  forceLogoutSuppressed = value;
};

const forceLogout = (message) => {
  if (typeof window === "undefined" || forceLogoutSuppressed) {
    return;
  }

  const onLoginPage = window.location.pathname === "/login";

  // Already on /login - the login form's own error handling shows the message,
  // so skip the generic forbidden toast here to avoid a duplicate error.
  if (!onLoginPage) {
    toast.error(message || ERROR_MESSAGES.forbidden);
  }

  clearAppStorage();

  if (!onLoginPage) {
    setTimeout(() => {
      window.location.href = "/login";
    }, FORBIDDEN_REDIRECT_DELAY);
  }
};

const axiosInstance = axios.create({
  headers: {
    accept: "application/json",
    UtcOffsetInSecond: UtcOffsetInSecond,
    AppVersion: AppVersion,
  },
});

const handleResponse = (response) => response.data;

const mapStatusToCode = (status) => {
  switch (status) {
    case 400:
      return "BAD_REQUEST";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 500:
      return "SERVER_ERROR";
    default:
      return "API_ERROR";
  }
};

const mapStatusToMessage = (status) => {
  switch (status) {
    case 400:
      return ERROR_MESSAGES.badRequest;
    case 401:
      return ERROR_MESSAGES.unauthorized;
    case 403:
      return ERROR_MESSAGES.forbidden;
    case 404:
      return ERROR_MESSAGES.notFound;
    case 500:
      return ERROR_MESSAGES.genericError;
    default:
      return ERROR_MESSAGES.genericError;
  }
};

const handleError = (error) => {
  const toastSuppressed400APIs = ["CreateNewStripePayment", "VerifyOTP"]; // APIs that should not show toast messages
  const toastSuppressed401APIs = ["GetPageInviteByToken" , "Login", "Signup"]; // APIs that should not show toast messages
  const unverifiedEmailAPIs = ["Login", "Signup"]; // APIs that handle unverified email in components
  
  if (error.response) {
    const { status, data } = error.response;
    const errorMessage = data?.message || ERROR_MESSAGES.genericError;

    if (status === 403) {
      forceLogout(errorMessage || mapStatusToMessage(status));
      return normalizeServiceError(error, {
        message: errorMessage || mapStatusToMessage(status),
        code: mapStatusToCode(status),
        status,
        details: data,
        apiName: data?.apiName,
      });
    }

    if (errorMessage === ERROR_MESSAGES.multipleLogin) {
      return normalizeServiceError(error, {
        message: ERROR_MESSAGES.sessionExpired,
        code: "AUTH_SESSION_EXPIRED",
        status,
        details: data,
        apiName: data?.apiName,
      });
    }

    if (errorMessage === ERROR_MESSAGES.emailNotVerified && unverifiedEmailAPIs.includes(data?.apiName)) {
      return normalizeServiceError(error, {
        message: errorMessage,
        code: "AUTH_UNVERIFIED_EMAIL",
        status,
        details: data,
        apiName: data?.apiName,
      });
    }

    return normalizeServiceError(error, {
      message: errorMessage || mapStatusToMessage(status),
      code: mapStatusToCode(status),
      status,
      details: data,
      apiName: data?.apiName,
    });
  } 
  else if (error.request) {
    return normalizeServiceError(error, {
      message: ERROR_MESSAGES.serverError,
      code: "NETWORK_ERROR",
      status: 0,
    });
  } 
  else {
    return normalizeServiceError(error, {
      message: ERROR_MESSAGES.requestError,
      code: "REQUEST_SETUP_ERROR",
      status: 0,
    });
  }
};


const ApiService = {
  get: async (url, params, customHeaders = {}, responseType = "json", requiresAuth = true) => {
    const { bearerToken, accessToken } = getTokens();

    try {
      const headers = {
        ...customHeaders,
        ...(requiresAuth && bearerToken  ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(requiresAuth && accessToken  ? { AccessToken: ` ${accessToken}` } : {}),
      };

      const response = await axiosInstance.get(url, {
        params,
        headers,
        responseType,
      });
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  post: async (url, data, customHeaders = {}, requiresAuth = true) => {
    
    const { bearerToken, accessToken } = getTokens();
    try {
      const headers = {
        ...customHeaders,
        ...(requiresAuth && bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(requiresAuth && accessToken  ? { AccessToken: ` ${accessToken}` } : {}),
      };

      const response = await axiosInstance.post(url, data, {
        headers,
      });

      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  put: async (url, data, customHeaders = {}, requiresAuth = true) => {
    const { bearerToken, accessToken } = getTokens();
    const fullURL = `${BASE_URL}${url}`;

    try {
      const headers = {
        ...customHeaders,
        ...(requiresAuth && bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(requiresAuth && accessToken  ? { AccessToken: ` ${accessToken}` } : {}),
      };

      const response = await axiosInstance.put(fullURL, data, {
        headers,
      });

      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  delete: async (url, customHeaders = {}, requiresAuth = true) => {
    const { bearerToken, accessToken } = getTokens();
    const fullURL = `${BASE_URL}${url}`;

    try {
      const headers = {
        ...customHeaders,
        ...(requiresAuth && bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(requiresAuth && accessToken  ? { AccessToken: ` ${accessToken}` } : {}),
      };

      const response = await axiosInstance.delete(fullURL, {
        headers,
      });

      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  deleteErrorLogs: async (url, data, customHeaders = {}, requiresAuth = true) => {
    const { bearerToken, accessToken } = getTokens();
    const fullURL = `${BASE_URL}${url}`;

    try {
      const headers = {
        ...customHeaders,
        ...(requiresAuth && bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(requiresAuth && accessToken  ? { AccessToken: ` ${accessToken}` } : {}),
      };

      const response = await axiosInstance.delete(fullURL, {
        headers,
        data,
      });

      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },
};

export default ApiService;
